import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import gsap from 'gsap';
import { getStartData, getLoadingProgress } from '../logic/startLogic';
import { ASSETS } from '../assetManifest';

const Snow = () => {
  const snowflakesRef = useRef([]);

  useEffect(() => {
    snowflakesRef.current.forEach((flake) => {
      gsap.set(flake, {
        y: -20,
        x: `${Math.random() * 140 - 20}vw`
      });

      gsap.to(flake, {
        y: '100vh',
        duration: 'random(2, 5)',
        repeat: -1,
        ease: 'none',
        delay: 'random(0, 2)'
      });

      gsap.to(flake, {
        x: `+=${Math.random() * 50}vw`,
        duration: 'random(2, 4)',
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
      });
    });
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {Array.from({ length: 50 }).map((_, i) => (
        <div
          key={i}
          ref={el => snowflakesRef.current[i] = el}
          className="absolute"
          style={{
            width: `${Math.random() * 3 + 1}px`,
            height: `${Math.random() * 3 + 1}px`,
            opacity: Math.random() * 0.7 + 0.3,
            backgroundColor: 'white',
            borderRadius: '50%'
          }}
        />
      ))}
    </div>
  );
};

function Home({ app }) {
  const [isStarting, setIsStarting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const homeBackgroundRef = useRef(null);
  const startButtonRef = useRef(null);
  const titleRef = useRef(null);
  const titleGlowRef = useRef(null);
  const whiteOverlayRef = useRef(null);
  const homeData = getStartData();
  const backgroundTweenRef = useRef(null);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      const progress = getLoadingProgress();
      setLoadingProgress(progress);
      if (progress >= 100) {
        setIsLoading(false);
        clearInterval(progressInterval);
      }
    }, 10);

    gsap.to(whiteOverlayRef.current, {
      opacity: 0,
      duration: 2,
      ease: 'power2.inOut'
    });

    backgroundTweenRef.current = gsap.to(homeBackgroundRef.current, {
      backgroundPosition: '100% 100%',
      duration: 20,
      repeat: -1,
      yoyo: true,
      ease: 'none'
    });

    gsap.to(startButtonRef.current, {
      opacity: 0.5,
      duration: 1,
      repeat: -1,
      yoyo: true,
      ease: 'power1.inOut'
    });

    const titleTimeline = gsap.timeline();

    titleTimeline.from(titleRef.current, {
      opacity: 0,
      y: -50,
      duration: 2,
      ease: 'power2.out'
    });

    return () => {
      clearInterval(progressInterval);
    };
  }, []);

  const handleStart = () => {
    if (isStarting || isLoading) return;
    setIsStarting(true);

    backgroundTweenRef.current.kill();

    const tl = gsap.timeline({
      onComplete: () => app.goToMenu()
    });

    tl.to([titleRef.current, startButtonRef.current], {
      opacity: 0,
      duration: 1,
      ease: 'power2.inOut'
    }, 0);

    tl.to(whiteOverlayRef.current, {
      opacity: 1,
      duration: 1,
      ease: 'power2.inOut'
    }, 0);

    tl.to(homeBackgroundRef.current, {
      scale: 1.2,
      duration: 1,
      ease: 'power2.inOut'
    }, 0);
  };

  return (
    <div
      ref={homeBackgroundRef}
      className="bg-cover h-screen relative cursor-pointer"
      style={{
        backgroundImage: `url(${ASSETS.backgrounds.start?.path})`
      }}
      onClick={handleStart}
    >
      <Snow />
      <div ref={whiteOverlayRef} className="absolute inset-0 bg-white z-50"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/50"></div>
      <div className="absolute inset-0"></div>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div ref={titleGlowRef} className="absolute w-[390px] h-[844px] rounded-full" />
        <img
          ref={titleRef}
          src={ASSETS.logos.title.path}
          alt="Shadow Realm of the Crimson Curse"
          className="mb-10 max-w-[300px] w-full"
        />
        {isLoading ? (
          <div className="relative w-80">
            <div className="absolute -inset-1 rounded-lg"></div>
            <div className="relative w-full h-3 bg-gradient-to-r from-gray-900/90 via-gray-800/90 to-gray-900/90 rounded-lg overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-700 via-blue-500 to-purple-700 relative"
                style={{ 
                  width: `${loadingProgress}%`,
                  transition: 'width 0.3s ease-out'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
              </div>
            </div>
            <div className="text-white text-sm mt-2 text-center font-light tracking-wider">
              Loading... {Math.round(loadingProgress)}%
            </div>
          </div>
        ) : (
          <div
            ref={startButtonRef}
            className={`text-2xl text-white flex items-center gap-2 ${isStarting ? 'pointer-events-none' : ''}`}
            style={{ fontFamily: homeData.fonts.ui }}
          >
            Press to Start <ArrowRight size={24} />
          </div>
        )}
      </div>
    </div>
  );
}

export default Home;
